"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { ClearButton } from "@/components/ui/clear-button"

const SelectRoot = SelectPrimitive.Root

// Context to pass validation attributes, id, ref, and clear behaviour from Select to SelectTrigger
const SelectContext = React.createContext<{
  ref?: React.Ref<HTMLButtonElement>
  id?: string
  "data-show-error"?: boolean
  "aria-invalid"?: boolean
  "aria-describedby"?: string
  showClear?: boolean
  onClear?: () => void
}>({})

const Select = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
    onBlur?: () => void
    id?: string
    /** When true, shows an "x" affordance to clear the current selection (emits an empty string). */
    allowClear?: boolean
    "data-show-error"?: boolean
    "aria-invalid"?: boolean
    "aria-describedby"?: string
  }
>(({ onOpenChange, onBlur, id, allowClear, "data-show-error": showError, "aria-invalid": invalid, "aria-describedby": describedBy, ...props }, ref) => {
  const handleOpenChange = React.useCallback((open: boolean) => {
    if (!open && onBlur) {
      onBlur()
    }
    onOpenChange?.(open)
  }, [onOpenChange, onBlur])

  const { value, onValueChange, disabled } = props
  const handleClear = React.useCallback(() => {
    onValueChange?.("")
    onBlur?.()
  }, [onValueChange, onBlur])

  const contextValue = React.useMemo(() => ({
    ref,
    id,
    "data-show-error": showError,
    "aria-invalid": invalid,
    "aria-describedby": describedBy,
    // Only offer clearing when enabled, a value is set, and the control is editable.
    showClear: !!allowClear && !!value && !disabled,
    onClear: handleClear
  }), [ref, id, showError, invalid, describedBy, allowClear, value, disabled, handleClear])

  return (
    <SelectContext.Provider value={contextValue}>
      <SelectRoot onOpenChange={handleOpenChange} {...props} />
    </SelectContext.Provider>
  )
})
Select.displayName = "Select"

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const triggerFocusClasses = "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
const triggerDisabledClasses = "disabled:cursor-not-allowed disabled:opacity-50"
const triggerErrorClasses = "data-[show-error]:aria-[invalid=true]:border-destructive data-[show-error]:aria-[invalid=true]:ring-destructive"

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const context = React.useContext(SelectContext)

  const trigger = (
    <SelectPrimitive.Trigger
      ref={context.ref || ref}
      id={context.id}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground [&>span]:line-clamp-1",
        triggerFocusClasses,
        triggerDisabledClasses,
        triggerErrorClasses,
        context.showClear && "pr-12",
        className
      )}
      data-show-error={context["data-show-error"]}
      aria-invalid={context["aria-invalid"]}
      aria-describedby={context["aria-describedby"]}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )

  // Only introduce the positioning wrapper when a clear affordance is actually rendered, so
  // non-clearable triggers keep their original DOM/layout (e.g. fixed-width SelectTriggers).
  if (!context.showClear) {
    return trigger
  }

  return (
    <div className="relative w-full">
      {trigger}
      <ClearButton onClear={() => context.onClear?.()} />
    </div>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItemText = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ItemText>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ItemText>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ItemText
    ref={ref}
    className={cn(className)}
    {...props}
  />
))
SelectItemText.displayName = SelectPrimitive.ItemText.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  // Recursively check if children contains SelectItemText component
  const hasSelectItemText = React.useMemo(() => {
    const findSelectItemText = (children: React.ReactNode): boolean => {
      return React.Children.toArray(children).some(child => {
        if (React.isValidElement(child)) {
          // Check if this is SelectItemText by displayName
          const childType = child.type as React.ComponentType
          if (childType?.displayName === SelectItemText.displayName) {
            return true
          }
          // Recursively check child's children
          if (child.props && typeof child.props === 'object' && 'children' in child.props) {
            return findSelectItemText(child.props.children as React.ReactNode)
          }
        }
        return false
      })
    }
    return findSelectItemText(children)
  }, [children])

  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      {hasSelectItemText ? (
        // Rich content with SelectItemText
        children
      ) : (
        // Simple content - wrap in SelectPrimitive.ItemText
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      )}
    </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectItemText,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
